import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL,
  PREMIUM_PAYMENT_TRUST_LINE,
  PREMIUM_PRICE_PERIOD_LABEL,
  PREMIUM_UPGRADE_CTA_LABEL,
} from '../../constants/pricing';
import { useAuth } from '../../AuthContext';
import { usePremiumUpgrade } from '../../hooks/usePremiumUpgrade';
import { resolveUserTier } from '../../utils/analytics';
import CheckoutErrorNotice from '../CheckoutErrorNotice/CheckoutErrorNotice';
import {
  trackMapPaywallExposure,
  trackSelectPromotionMapV2,
  trackViewPromotionMapV2,
} from '../../utils/analyticsAB';
import SocialAuthButtons from '../SocialAuth/SocialAuthButtons';
import './MapPaywallSheet.css';

const MapPaywallSheet = ({ isOpen, onClose, user = null, sheetRef }) => {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithApple, refreshUser } = useAuth();
  const [authError, setAuthError] = useState(null);
  const headingRef = useRef(null);
  const isAuthenticated = user !== null;
  const userTier = resolveUserTier(user);

  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath: '/map',
    source: 'map_paywall',
    placement: 'map_paywall',
    feature: 'map',
  });

  useEffect(() => {
    if (!isOpen) return;
    trackViewPromotionMapV2(userTier);
    trackMapPaywallExposure();
  }, [isOpen, userTier]);

  useEffect(() => {
    if (isOpen && headingRef.current) {
      headingRef.current.focus();
    }
  }, [isOpen, isAuthenticated]);

  if (!isOpen) return null;

  const handleRegisterEmail = () => {
    trackSelectPromotionMapV2('register');
    navigate('/register?next=/map');
  };

  const handleLogin = () => {
    trackSelectPromotionMapV2('login');
    navigate('/login?next=/map');
  };

  const handleUpgradeClick = () => {
    trackSelectPromotionMapV2('upgrade');
    startCheckout();
  };

  const handleSocialSignedIn = async (_result, method) => {
    trackSelectPromotionMapV2('register', method ? { method } : {});
    setAuthError(null);
    await refreshUser();
  };

  return (
    <aside
      className="map-paywall-sheet"
      role="dialog"
      aria-modal="false"
      aria-labelledby="map-paywall-title"
      tabIndex={-1}
      ref={sheetRef}
    >
      <button type="button" className="map-paywall-sheet__close" onClick={onClose} aria-label="Luk">
        ×
      </button>

      {!isAuthenticated ? (
        <>
          <h2
            id="map-paywall-title"
            className="map-paywall-sheet__title"
            tabIndex={-1}
            ref={headingRef}
          >
            Se institutionerne i dit nabolag på kortet
          </h2>
          <p className="map-paywall-sheet__lead">
            Kortet er en Premium-funktion. Opret konto på få sekunder — derefter én betaling.
            En basis-konto låser ikke kortet op.
          </p>
          <div className="map-paywall-sheet__price">
            <span className="map-paywall-sheet__amount">{PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL}</span>
            <span className="map-paywall-sheet__period">{PREMIUM_PRICE_PERIOD_LABEL}</span>
          </div>
          <p className="map-paywall-sheet__trust">{PREMIUM_PAYMENT_TRUST_LINE}</p>
          <ul className="map-paywall-sheet__bullets">
            <li>Overblik over institutioner i nærområdet</li>
            <li>Find steder tæt på jer — ikke kun på en liste</li>
            <li>Sammenlign observationer, historik og hvilken institution der står stærkest</li>
          </ul>
          <SocialAuthButtons
            mode="register"
            hideDivider
            loginWithGoogle={loginWithGoogle}
            loginWithApple={loginWithApple}
            onSignedIn={handleSocialSignedIn}
            onError={(msg) => setAuthError(msg)}
          />
          {authError && <p className="map-paywall-sheet__error">{authError}</p>}
          <button type="button" className="map-paywall-sheet__secondary" onClick={handleRegisterEmail}>
            Opret med e-mail
          </button>
          <p className="map-paywall-sheet__login">
            Har du allerede konto?{' '}
            <button type="button" className="map-paywall-sheet__text-btn" onClick={handleLogin}>
              Log ind
            </button>
          </p>
        </>
      ) : (
        <>
          <h2
            id="map-paywall-title"
            className="map-paywall-sheet__title"
            tabIndex={-1}
            ref={headingRef}
          >
            Lås det kort op, du kigger på
          </h2>
          <p className="map-paywall-sheet__lead">
            Du er logget ind. Premium giver fuld kortvisning med Google Maps i ét år.
          </p>
          <div className="map-paywall-sheet__price">
            <span className="map-paywall-sheet__amount">{PREMIUM_ANNUAL_PRICE_AMOUNT_LABEL}</span>
            <span className="map-paywall-sheet__period">{PREMIUM_PRICE_PERIOD_LABEL}</span>
          </div>
          <p className="map-paywall-sheet__trust">{PREMIUM_PAYMENT_TRUST_LINE}</p>
          <ul className="map-paywall-sheet__bullets">
            <li>Overblik over institutioner i nærområdet</li>
            <li>Find steder tæt på jer — ikke kun på en liste</li>
            <li>Sammenlign observationer, historik og hvilken institution der står stærkest</li>
          </ul>
          <CheckoutErrorNotice message={checkoutError} onRetry={handleUpgradeClick} />
          <button
            type="button"
            className="map-paywall-sheet__primary"
            onClick={handleUpgradeClick}
            disabled={isStartingCheckout}
          >
            {isStartingCheckout ? 'Åbner betaling...' : PREMIUM_UPGRADE_CTA_LABEL}
          </button>
        </>
      )}
    </aside>
  );
};

export default MapPaywallSheet;
